import React, { useState, useEffect } from 'react';
import { Table, Card, Spinner, Badge, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { CardContent } from '../components/Card';
import { useLanguage } from '../context/LanguageContext';
import { usePageMetadata } from '../context/PageMetadataContext';
import AccountLabel from '../components/AccountLabel';
import CopyIcon from '../components/CopyIcon';
import axios from 'axios';
import { formatNumber } from '../utils/format';
import { timelib } from '../utils/time';
import AdSenseUnit from '../components/AdsenseUnit';
import AdUnit from '../components/AdUnit';

interface LargestTransaction {
  account: string;
  amount: number;
  created_at: string;
  transaction_hash: string;
}

const LargestTx: React.FC = () => {
  const { t,language } = useLanguage();
  const { setHeading, setTitle, setDescription } = usePageMetadata();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [transactions, setTransactions] = useState<LargestTransaction[]>([]);

  // Set metadata for page
  useEffect(() => {
    setHeading(t('largest_tx.heading'));
    setTitle(t('largest_tx.title'));
    setDescription(t('largest_tx.description'));
  }, [setHeading, setTitle, setDescription, t]);

  // Fetch largest transactions
  useEffect(() => {
    const fetchLargestTransactions = async () => {
      try {
        setLoading(true);
        const response = await axios.get('https://api.piscan.io/data/largest-transactions');
        
        if (response.data && response.data.data) {
          setTransactions(response.data.data);
        } else {
          setError(t('largest_tx.no_data'));
        }
      } catch (err) {
        console.error('Error fetching largest transactions:', err);
        setError(t('largest_tx.fetch_error'));
      } finally {
        setLoading(false);
      }
    };

    fetchLargestTransactions();
  }, [t]);

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

    

  return (
    <div className="largest-tx-page">
    
       
          <p className="text-muted">{t('largest_tx.description')}</p>
          
          <Row className="mt-4">
            <Col md={6} lg={4} className="mb-3">
              <Card className="h-100 border-0 shadow-sm">
                <CardContent>
                  <h5 className="mb-3 stats-number">
                    <i className="bi bi-cash-coin text-primary me-2"></i>
                    {t('largest_tx.largest_amount')}
                  </h5>
                  {!loading && transactions.length > 0 ? (
                    <div className="d-flex align-items-center">
                      <span className="display-5 stats-number">{formatNumber(transactions[0]?.amount || 0,language)}</span>
                      <span className="ms-2 text-muted">π</span>
                    </div>
                  ) : (
                    <div className="placeholder-glow">
                      <div className="placeholder col-8"></div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </Col>
            <Col md={6} lg={4} className="mb-3">
              <Card className="h-100 border-0 shadow-sm">
                <CardContent>
                  <h5 className="mb-3 stats-number">
                    <i className="bi bi-calendar-check text-success me-2"></i>
                    {t('largest_tx.latest_large_tx')}
                  </h5>
                  {!loading && transactions.length > 0 ? (
                    <div>
                      <div className="small text-muted">{timelib.timeFormat(transactions[0]?.created_at,language)}</div>
                      <div className="mt-2">
                        <Link to={`/tx/${transactions[0]?.transaction_hash}`} className="text-decoration-none">
                          {transactions[0]?.transaction_hash.substring(0, 16)}...
                        </Link>
                      </div>
                    </div>
                  ) : (
                    <div className="placeholder-glow">
                      <div className="placeholder col-8"></div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </Col>
            <Col md={12} lg={4} className="mb-3">
              <Card className="h-100 border-0 shadow-sm">
                <CardContent>
                  <h5 className="mb-3 stats-number">
                    <i className="bi bi-activity text-danger me-2"></i>
                    {t('largest_tx.total_value')}
                  </h5>
                  {!loading && transactions.length > 0 ? (
                    <div className="d-flex align-items-center">
                      <span className="display-5 stats-number">
                        {formatNumber(
                          transactions.reduce((sum, tx) => sum + tx.amount, 0),
                          language
                        )}
                      </span>
                      <span className="ms-2 text-muted">π</span>
                    </div>
                  ) : (
                    <div className="placeholder-glow">
                      <div className="placeholder col-8"></div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </Col>
          </Row>
          <div className="row g-3 mb-4  d-block d-md-none">
        {/* <AdSenseUnit/> */}
        {/* <AdUnit
                  className="67cc9f95aa72d3d47fe128c2"
                  width="300px"
                  height="100px"
                  centered={true}
                /> */}
      </div>
      

      <Card>
        <CardContent>
          <h3 className="mb-4">{t('largest_tx.list_heading')}</h3>
          
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">{t('largest_tx.loading')}</p>
            </div>
          ) : error ? (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover>
                <thead>
                  <tr>
                    <th>{t('largest_tx.rank')}</th>
                    <th>{t('largest_tx.transaction')}</th>
                    <th>{t('largest_tx.account')}</th>
                    <th className="text-end">{t('largest_tx.amount')}</th>
                    <th>{t('largest_tx.date')}</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tx, index) => (
                    <tr key={tx.transaction_hash}>
                      <td>
                        {index === 0 ? (
                          <Badge bg="warning" text="dark">#{index + 1}</Badge>
                        ) : index === 1 ? (
                          <Badge bg="secondary" text="light">#{index + 1}</Badge>
                        ) : index === 2 ? (
                          <Badge bg="danger" text="light">#{index + 1}</Badge>
                        ) : (
                          <Badge bg="light" text="dark">#{index + 1}</Badge>
                        )}
                      </td>
                      <td>
                        <Link to={`/tx/${tx.transaction_hash}`} className="text-decoration-none">
                          {tx.transaction_hash.substring(0, 3)}...{tx.transaction_hash.substring(tx.transaction_hash.length - 3)}
                        </Link> {" "}
                        <CopyIcon textToCopy={tx.transaction_hash} />
                      </td>
                      <td>
                        <Link to={`/account/${tx.account}`} className="text-decoration-none">
                          <AccountLabel
                            account={tx.account}
                            shorten={true}
                          />
                        </Link>
                      </td>
                      <td className="text-end fw-bold">
                        {formatNumber(tx.amount,language)} π
                      </td>
                      <td>
                        <div>{formatDate(tx.created_at)}</div>
                        <div className="small text-muted">{timelib.timeFormat(tx.created_at,language)}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LargestTx;